import { FUND_CSV_COLUMNS, toCsv } from './fundCsv.js';
import { investorTypeLabel } from './investorType.js';
import { formatRoundLabel, formatRoundMonth } from './roundDate.js';
/** The key under which an item's data holds the note. */
export const NOTE_KEY = 'note';
/** "Arlequin AI · Series A · Sep 2026", the grid's Recent investments line. */
export function recentInvestmentLine(round) {
    return [round.companyName, formatRoundLabel(round.roundLabel), formatRoundMonth(round.month)]
        .filter((part) => part !== '')
        .join(' · ');
}
/**
 * "Anton Waitz → Sonali De Rycker": who at Project A intros, to whom at the
 * fund. Either side alone is just that name.
 */
export function introRoute(memberName, investorName) {
    return [memberName, investorName].filter((name) => name !== null && name !== '').join(' → ');
}
/**
 * The vocabulary, in the order a new list shows it: the founder's columns
 * first, then the internal ones. The keys are the grid's column ids where the
 * grid has the column, so a list view can map one onto the other.
 */
export const LIST_COLUMNS = {
    name: { label: 'Fund', internal: false, text: ({ fund }) => fund.name },
    website: { label: 'Website', internal: false, text: ({ fund }) => fund.website ?? '' },
    stagesInvests: {
        label: 'Stages (invests)',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.stagesInvests.text(fund),
    },
    stagesLeads: {
        label: 'Stages (leads)',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.stagesLeads.text(fund),
    },
    hqCountries: {
        label: 'HQ',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.hqCountries.text(fund),
    },
    regions: {
        label: 'Regions',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.regions.text(fund),
    },
    investorType: {
        label: 'Type',
        internal: false,
        text: ({ fund }) => investorTypeLabel(fund.investorType),
    },
    sectors: {
        label: 'Sectors',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.sectors.text(fund),
    },
    recentInvestments: {
        label: 'Recent investments',
        internal: false,
        text: ({ fund }) => fund.recentInvestments.map(recentInvestmentLine).join('; '),
    },
    notableInvestments: {
        label: 'Notable investments',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.notableInvestments.text(fund),
    },
    cheque: {
        label: 'Cheque (€)',
        internal: false,
        text: ({ fund }) => FUND_CSV_COLUMNS.cheque.text(fund),
    },
    suggestedIntro: {
        label: 'Suggested intro',
        internal: false,
        text: (item) => introRoute(item.suggestedMemberName, item.suggestedInvestorName),
    },
    note: { label: 'Note', internal: false, text: (item) => item.data[NOTE_KEY] ?? '' },
    tier: { label: 'Tier', internal: true, text: ({ fund }) => FUND_CSV_COLUMNS.tier.text(fund) },
    owners: {
        label: 'Owners',
        internal: true,
        text: ({ fund }) => FUND_CSV_COLUMNS.owners.text(fund),
    },
    whoKnows: {
        label: 'Who knows whom',
        internal: true,
        text: ({ fund }) => FUND_CSV_COLUMNS.whoKnows.text(fund),
    },
    observed: {
        label: 'Observed',
        // Evidence behind the merged columns: off for a founder's file, on request.
        internal: true,
        text: ({ fund }) => FUND_CSV_COLUMNS.observed.text(fund),
    },
    notes: { label: 'Notes', internal: true, text: ({ fund }) => fund.notes ?? '' },
    chequeEstimate: {
        label: 'Cheque (estimated)',
        // A guess from the rounds, never a figure to put in front of a founder; off
        // by default, on request.
        internal: true,
        text: ({ fund }) => FUND_CSV_COLUMNS.chequeEstimate.text(fund),
    },
    coInvested: {
        label: 'Co-invested',
        internal: true,
        // The record only, as the grid's export: Dealroom-only sightings are evidence.
        text: ({ fund }) => fund.coInvestments
            .filter((row) => !row.observedOnly)
            .map((row) => `${row.company} (${row.status})`)
            .join('; '),
    },
};
/** Every key, in the vocabulary's order: what a column picker lists. */
export const LIST_COLUMN_KEYS = Object.keys(LIST_COLUMNS);
/** A new list starts with every founder column on, in the vocabulary's order. */
export const DEFAULT_LIST_COLUMNS = LIST_COLUMN_KEYS.filter((key) => !LIST_COLUMNS[key].internal);
export function isListColumnKey(key) {
    return Object.hasOwn(LIST_COLUMNS, key);
}
/**
 * An extra column's key, `x` and eight hex digits: never one of the
 * vocabulary's, so the two share one ordered column set without either
 * shadowing the other.
 */
export function isExtraColumnKey(key) {
    return /^x[0-9a-f]{8}$/.test(key);
}
/**
 * The stored column set as the columns this code can render, each once, in
 * the stored order: vocabulary keys and this list's extra columns. A key a
 * later vocabulary dropped, or an extra column since removed, is skipped
 * rather than failing an export.
 */
export function resolveListColumns(stored, extraColumns) {
    const extras = new Map(extraColumns.map((column) => [column.key, column]));
    const seen = new Set();
    const resolved = [];
    for (const key of stored) {
        if (seen.has(key))
            continue;
        seen.add(key);
        if (isListColumnKey(key)) {
            resolved.push({ key, extra: false, ...LIST_COLUMNS[key] });
            continue;
        }
        const extra = extras.get(key);
        if (extra !== undefined) {
            resolved.push({
                key,
                extra: true,
                label: extra.label,
                internal: false,
                text: (item) => item.data[key] ?? '',
            });
        }
    }
    return resolved;
}
/**
 * The list as CSV: the list's columns, in its order, one row per item, read
 * off the live funds. A new list's column set has no internal column, so a
 * founder's file is clean unless somebody turned one on.
 */
export function listToCsv(list, items) {
    const columns = resolveListColumns(list.columns, list.extraColumns);
    return toCsv(columns.map((column) => column.label), items.map((item) => columns.map((column) => column.text(item))));
}
//# sourceMappingURL=listColumns.js.map